import { useState, useEffect } from 'react';
import { useVariance } from '../hooks/useVariance';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorCard      from '../components/ErrorCard';


function fmt(n) {
  if (!n && n !== 0) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', maximumFractionDigits: 0
  }).format(n);
}

function BudgetVsActual() {
  const [month, setMonth] = useState('');
  const [year, setYear] = useState('');
  const { data, loading, error, refresh } = useVariance(month || null, year || null);

  const MONTHS = ['', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

// ADD state inside BudgetVsActual():
const [sending,  setSending]  = useState(false);
const [emailMsg, setEmailMsg] = useState('');

const sendEmail = async () => {
  setSending(true); setEmailMsg('');
  try {
    const r = await fetch('/api/email/send', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ month: month || null, year: year || null }),
    });
    const d = await r.json();
    if (!r.ok) throw new Error(d.error);
    setEmailMsg('✓ Email sent successfully.');
  } catch (e) {
    setEmailMsg(`Error: ${e.message}`);
  } finally {
    setSending(false);
  }
};




//  if (loading) return <div className="page"><p>Loading variance data...</p></div>;
//  if (error) return <div className="page"><p style={{ color: '#BE123C' }}>Error: {error}</p></div>;
 if (loading) return <div className="page"><LoadingSpinner message="Loading variance data from QuickBooks..." /></div>;
 if (error)   return <div className="page"><ErrorCard error={error} onRetry={refresh} /></div>;
 if (!data) return null;

  const { period, summary, variances } = data;

  // --- CLASSIFICATION LOGIC BASED ON 'SECTION' ---
const incomeItems = variances.filter(v => {
  // We check v.section now because that is what your backend sends
  const s = v.section?.toUpperCase() || '';
  return s.includes('INCOME') || s.includes('REVENUE');
});

const cogsItems = variances.filter(v => {
  const s = v.section?.toUpperCase() || '';
  return s.includes('COGS') || s.includes('COST OF GOODS');
});

const expenseItems = variances.filter(v => {
  const s = v.section?.toUpperCase() || '';
  return s.includes('EXPENSE') && !cogsItems.includes(v);
});

// This will now be empty because we are matching the correct field!
const otherItems = variances.filter(v => 
  !incomeItems.includes(v) && !cogsItems.includes(v) && !expenseItems.includes(v)
);

  // COGS totals
  const cogsBudget   = cogsItems.reduce((sum, v) => sum + v.budget, 0);
  const cogsActual   = cogsItems.reduce((sum, v) => sum + v.actual, 0);
  const cogsVariance = cogsBudget - cogsActual; // favorable = under budget

  // Gross Profit = Income - COGS
  const gpBudget   = summary.budgetIncome - cogsBudget;
  const gpActual   = summary.actualIncome - cogsActual;
  const gpVariance = gpActual - gpBudget;       // favorable = above budget

  return (
    <div className="page">
      {/* 1. Header & Controls */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h2>Budget vs Actual — {period.month} {period.year}</h2>

      <div style={{ display:'flex', gap:'0.5rem', alignItems:'center' }}>
        <select value={month} onChange={e => setMonth(e.target.value)}
          style={{ padding:'0.4rem', borderRadius:4, border:'1px solid #CBD5E1' }}>
          <option value="">Current Month</option>
          <option value="1">January</option>
          <option value="2">February</option>
          <option value="3">March</option>
          <option value="4">April</option>
          <option value="5">May</option>
          <option value="6">June</option>
          <option value="7">July</option>
          <option value="8">August</option>
          <option value="9">September</option>
          <option value="10">October</option>
          <option value="11">November</option>
          <option value="12">December</option>
        </select>
        <input
          type="number"
          placeholder="Year"
          defaultValue={year || new Date().getFullYear()}
          onBlur={e => {
            const val = parseInt(e.target.value);
            if (val > 2000 && val < 2100) setYear(val);
          }}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              const val = parseInt(e.target.value);
              if (val > 2000 && val < 2100) setYear(val);
            }
          }}
          style={{ width:85, padding:'0.4rem', borderRadius:4, border:'1px solid #CBD5E1' }}
        />
        <button onClick={refresh}
          style={{ padding:'0.4rem 1rem', background:'#0E7C7B', color:'#fff',
                  border:'none', borderRadius:4, cursor:'pointer', fontWeight:600 }}>
          Go
        </button>

        <button
          onClick={sendEmail}
          disabled={sending}
          style={{ padding:'0.4rem 1rem',
                  background: sending ? '#94A3B8' : '#1B2A4A',
                  color:'#fff', border:'none', borderRadius:4,
                  cursor: sending ? 'default' : 'pointer',
                  fontSize:'0.85rem', fontWeight:600 }}>
          {sending ? 'Sending...' : '📧 Email Report'}
        </button>


      </div>
      </div>

  
      {emailMsg && (
        <div style={{ marginTop:'0.5rem', fontSize:'0.85rem', padding:'0.4rem 0.75rem',
                      borderRadius:4,
                      background: emailMsg.startsWith('✓') ? '#DCFCE7' : '#FFE4E6',
                      color:      emailMsg.startsWith('✓') ? '#15803D' : '#BE123C' }}>
          {emailMsg}
        </div>
      )}

      {/* 2. Top Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', marginBottom: '2rem' }}>
        {[
          { label: 'Total Income', budget: summary.budgetIncome, actual: summary.actualIncome },
          { label: 'Total Expenses', budget: summary.budgetExpenses, actual: summary.actualExpenses },
          { label: 'Net Income', budget: summary.budgetNet, actual: summary.actualNet },
        ].map(({ label, budget, actual }) => {
          const isExpense = label.includes('Expenses');
          const variance = isExpense ? budget - actual : actual - budget;
          const isGood = variance >= 0;
          return (
            <div key={label} style={{
              background: '#fff', padding: '1.25rem', borderRadius: 8,
              borderLeft: `5px solid ${isGood ? '#15803D' : '#BE123C'}`,
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
            }}>
              <div style={{ fontSize: '0.85rem', color: '#64748B', fontWeight: 600, marginBottom: '0.5rem' }}>{label}</div>
              <div style={{ fontSize: '1.25rem', fontWeight: 800, color: '#1B2A4A' }}>{fmt(actual)}</div>
              <div style={{ fontSize: '0.9rem', marginTop: '0.4rem', color: isGood ? '#15803D' : '#BE123C', fontWeight: 500 }}>
                {isGood ? '▲' : '▼'} {fmt(Math.abs(variance))} vs budget
              </div>
            </div>
          );
        })}
      </div>

      {/* 3. P&L Statement Table */}
      <div style={{ background: '#fff', borderRadius: 8, boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.95rem' }}>
          <thead>
            <tr style={{ background: '#1B2A4A', color: '#fff' }}>
              <th style={{ padding: '1rem', textAlign: 'left' }}>Account Details</th>
              <th style={{ padding: '1rem', textAlign: 'right' }}>Budget</th>
              <th style={{ padding: '1rem', textAlign: 'right' }}>Actual</th>
              <th style={{ padding: '1rem', textAlign: 'right' }}>Variance</th>
            </tr>
          </thead>
          <tbody>
            
            {/* INCOME SECTION */}
            <tr style={{ background: '#F8FAFC' }}>
              <td colSpan="4" style={{ padding: '0.75rem 1rem', fontWeight: 700, color: '#475569', textTransform: 'uppercase', fontSize: '0.75rem' }}>Income</td>
            </tr>
            {incomeItems.map((v, i) => (
              <tr key={`inc-${i}`} style={{ borderBottom: '1px solid #F1F5F9' }}>
                <td style={{ padding: '0.75rem 1rem', paddingLeft: '2.5rem' }}>{v.category}</td>
                <td style={{ textAlign: 'right', padding: '0.75rem 1rem' }}>{fmt(v.budget)}</td>
                <td style={{ textAlign: 'right', padding: '0.75rem 1rem' }}>{fmt(v.actual)}</td>
                <td style={{ textAlign: 'right', padding: '0.75rem 1rem', fontWeight: 600, color: v.variance >= 0 ? '#15803D' : '#BE123C' }}>{v.variance >= 0 ? '▲ ' : '▼ '}{fmt(Math.abs(v.variance))}</td>
              </tr>
            ))}
            <tr style={{ fontWeight: 700, background: '#FDFDFD', borderBottom: '2px solid #E2E8F0' }}>
              <td style={{ padding: '0.75rem 1rem' }}>Total Income</td>
              <td style={{ textAlign: 'right', padding: '0.75rem 1rem' }}>{fmt(summary.budgetIncome)}</td>
              <td style={{ textAlign: 'right', padding: '0.75rem 1rem' }}>{fmt(summary.actualIncome)}</td>
              {(() => {
                const v = summary.actualIncome - summary.budgetIncome;
                return <td style={{ textAlign:'right', padding:'0.75rem 1rem', fontWeight:600, color: v>=0?'#15803D':'#BE123C' }}>{v>=0?'▲ ':'▼ '}{fmt(Math.abs(v))}</td>;
              })()}
            </tr>

            {/* COGS — single summary row */}
            {cogsItems.length > 0 && (
              <tr style={{ fontWeight: 700, background: '#FDFDFD', borderBottom: '1px solid #E2E8F0' }}>
                <td style={{ padding: '0.75rem 1rem' }}>Total Cost of Goods Sold</td>
                <td style={{ textAlign: 'right', padding: '0.75rem 1rem' }}>{fmt(cogsBudget)}</td>
                <td style={{ textAlign: 'right', padding: '0.75rem 1rem' }}>{fmt(cogsActual)}</td>
                <td style={{ textAlign: 'right', padding: '0.75rem 1rem', fontWeight: 600, color: cogsVariance >= 0 ? '#15803D' : '#BE123C' }}>
                  {cogsVariance >= 0 ? '▲ ' : '▼ '}{fmt(Math.abs(cogsVariance))}
                </td>
              </tr>
            )}

            {/* GROSS PROFIT */}
            {cogsItems.length > 0 && (
              <tr style={{ fontWeight: 800, background: '#F0FDF4', borderBottom: '2px solid #BBF7D0' }}>
                <td style={{ padding: '0.75rem 1rem', color: '#15803D' }}>Gross Profit</td>
                <td style={{ textAlign: 'right', padding: '0.75rem 1rem', color: '#15803D' }}>{fmt(gpBudget)}</td>
                <td style={{ textAlign: 'right', padding: '0.75rem 1rem', color: '#15803D' }}>{fmt(gpActual)}</td>
                <td style={{ textAlign: 'right', padding: '0.75rem 1rem', color: gpVariance >= 0 ? '#15803D' : '#BE123C' }}>
                  {gpVariance >= 0 ? '▲ ' : '▼ '}{fmt(Math.abs(gpVariance))}
                </td>
              </tr>
            )}

            {/* EXPENSES SECTION */}
            <tr style={{ background: '#F8FAFC' }}>
              <td colSpan="4" style={{ padding: '1.25rem 1rem 0.5rem 1rem', fontWeight: 700, color: '#475569', textTransform: 'uppercase', fontSize: '0.75rem' }}>Operating Expenses</td>
            </tr>
            {expenseItems.map((v, i) => (
              <tr key={`exp-${i}`} style={{ borderBottom: '1px solid #F1F5F9' }}>
                <td style={{ padding: '0.75rem 1rem', paddingLeft: '2.5rem' }}>{v.category}</td>
                <td style={{ textAlign: 'right', padding: '0.75rem 1rem' }}>{fmt(v.budget)}</td>
                <td style={{ textAlign: 'right', padding: '0.75rem 1rem' }}>{fmt(v.actual)}</td>
                <td style={{ textAlign: 'right', padding: '0.75rem 1rem', fontWeight: 600, color: v.variance >= 0 ? '#15803D' : '#BE123C' }}>{v.variance >= 0 ? '▲ ' : '▼ '}{fmt(Math.abs(v.variance))}</td>
              </tr>
            ))}
            <tr style={{ fontWeight: 700, background: '#FDFDFD', borderBottom: '2px solid #E2E8F0' }}>
              <td style={{ padding: '0.75rem 1rem' }}>Total Operating Expenses</td>
              <td style={{ textAlign: 'right', padding: '0.75rem 1rem' }}>{fmt(summary.budgetExpenses)}</td>
              <td style={{ textAlign: 'right', padding: '0.75rem 1rem' }}>{fmt(summary.actualExpenses)}</td>
              {(() => {
                const v = summary.budgetExpenses - summary.actualExpenses;
                return <td style={{ textAlign:'right', padding:'0.75rem 1rem', fontWeight:600, color: v>=0?'#15803D':'#BE123C' }}>{v>=0?'▲ ':'▼ '}{fmt(Math.abs(v))}</td>;
              })()}
            </tr>

            {/* UNCLASSIFIED SECTION */}
            {otherItems.length > 0 && (
              <>
                <tr style={{ background: '#FFF7ED' }}>
                  <td colSpan="4" style={{ padding: '1rem', fontWeight: 700, color: '#C2410C', fontSize: '0.75rem', textTransform: 'uppercase' }}>Unclassified Accounts</td>
                </tr>
                {otherItems.map((v, i) => (
                  <tr key={`oth-${i}`} style={{ borderBottom: '1px solid #F1F5F9' }}>
                    <td style={{ padding: '0.75rem 1rem', paddingLeft: '2.5rem' }}>{v.category} <span style={{fontSize: '0.7rem', color: '#94A3B8'}}>({v.type || 'None'})</span></td>
                    <td style={{ textAlign: 'right', padding: '0.75rem 1rem' }}>{fmt(v.budget)}</td>
                    <td style={{ textAlign: 'right', padding: '0.75rem 1rem' }}>{fmt(v.actual)}</td>
                    <td style={{ textAlign: 'right', padding: '0.75rem 1rem' }}>{fmt(v.variance)}</td>
                  </tr>
                ))}
              </>
            )}

            {/* NET INCOME FOOTER */}
            <tr style={{ background: '#1B2A4A', color: '#fff', fontWeight: 800, fontSize: '1rem' }}>
              <td style={{ padding: '1.25rem 1rem' }}>Net Operating Income</td>
              <td style={{ textAlign: 'right', padding: '1.25rem 1rem' }}>{fmt(summary.budgetNet)}</td>
              <td style={{ textAlign: 'right', padding: '1.25rem 1rem' }}>{fmt(summary.actualNet)}</td>
              {(() => {
                const v = summary.actualNet - summary.budgetNet;
                return <td style={{ textAlign:'right', padding:'1.25rem 1rem', fontWeight:800 }}>{v>=0?'▲ ':'▼ '}{fmt(Math.abs(v))}</td>;
              })()}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default BudgetVsActual;